import React, {useState} from "react";
import dayjs from "dayjs";
import {Popover, Table, TableColumnsType, Tag} from "antd";
import {CloseOutlined} from "@ant-design/icons";
import styles from "./BaechaListTable.module.scss";
import {Baecha} from "../../../types/Baecha";
import {
	formatCurrency,
	getBaechaPriceReceiveMethodStr,
	getBaechaStatusStr, getRegionPrefix,
	getShortBaechaPriceTypeStr,
	getStatusColor
} from "../../../util/format";
import userAPI from "../../../api/userAPI";
import {getWorkPeriodStr} from "../../../util/date";
import constants from "../../../constants";

interface BaechaListTableProps {
	list: Baecha[];
	onClickData:(baechaData: Baecha) => any;
}

export const BaechaListTable = ({ list, onClickData }: BaechaListTableProps) => {
	const [openPopover, setOpenPopover] = useState<{ type: 'balju' | 'suju'; baechaId: string } | null>(null);
	const [popupData, setPopupData] = useState<any>(undefined);

	const onOpenPopup = async (bool: boolean, type: 'balju' | 'suju', baechaId: string, userId?: string) => {
		if (bool && userId) {
			const res = await userAPI.fetchUserDataAPI(userId);
			setPopupData(res);
			setOpenPopover({ type, baechaId });
		} else {
			setPopupData(undefined);
			setOpenPopover(null);
		}
	};

	const onClosePopup = (e: any) => {
		e.preventDefault();
		setOpenPopover(null);
		setPopupData(undefined);
	};



	const columns: TableColumnsType<Baecha> = [
		{
			title: '번호',
			dataIndex: 'baechaNo',
			key: 'baechaNo',
			width: 60,
			align: 'center',
			ellipsis: true,
		},
		{
			title: '발주시간',
			dataIndex: 'baljuingStartDate',
			width: 110,
			align: 'center',
			ellipsis: true,
			render: (text: string) => (
				<div style={{ fontVariantNumeric: 'tabular-nums' }}>
					{/*{formatKorDate(text)} {dayjs(text).format('HH:mm')}*/}
					{dayjs(text).format('MM월 DD일')} {dayjs(text).format('HH:mm')}
				</div>
			)
		},
		{
			title: '상태',
			dataIndex: 'status',
			key: 'status',
			width: 70,
			align: 'center',
			render: (value: any, record: Baecha, index: number) => (
				<Tag color={getStatusColor(value)}>
					{getBaechaStatusStr(value)}
				</Tag>
			),
		},
		{
			title: '발주 배차방',
			dataIndex: 'baljuGroupName',
			key: 'baljuGroupName',
			width: 100,
			align: 'center',
			ellipsis: true,
			render: (value: any, record: Baecha, index: number) => {
				if (record.baljuGroupName === record.baljuingGroupName) return value;
				return (
					<>
						<div>{record.baljuingGroupName}</div>
						<div>{`← ${record.baljuGroupName}`}</div>
					</>
				);
			},
		},
		{
			title: '발주자',
			dataIndex: 'baljuMemberNickname',
			key: 'baljuMemberNickname',
			width: 150,
			align: 'center',
			ellipsis: true,
			onCell: (record) => ({
				onClick: (e) => {
					e.stopPropagation();
				},
				style: { cursor: 'pointer', color: '#1677ff', textDecoration: 'underline' },
			}),
			render: (value: any, record: Baecha, index: number) => (
				<Popover
					placement="top"
					content={(
						<a onClick={onClosePopup}>
							{popupData?.memberInfo?.filter((item: any) => item.status === 'ok').length > 0 && (
								<div>
									{popupData?.memberInfo?.filter((item: any) => item.status === 'ok')
										.map((item: any) => <div key={item.name}>{item.name}</div>)}
								</div>
							)}
						</a>
					)}
					trigger="click"
					title={(
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<div>{popupData?.userInfo?.name || ''}</div>
							<div>{popupData?.userInfo?.phoneNum || ''}</div>
						</div>
					)}
					open={openPopover?.type === 'balju' && openPopover?.baechaId === record.id}
					onOpenChange={(bool) => onOpenPopup(bool, 'balju', record.id, record.baljuUserId)}
				>
					<div className={styles.clickable} style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
						{!record.isHaveEvent
							&& record.baljuGroupId === constants.CALL_CENTER_GROUP_ID
							&& <CloseOutlined style={{ color: 'red' }} />}
						{record.baljuMemberNickname}
					</div>
				</Popover>
			),
		},
		{
			title: '수주자',
			dataIndex: 'sujuMemberNickname',
			key: 'sujuMemberNickname',
			width: 150,
			align: 'center',
			ellipsis: true,
			onCell: (record) => ({
				onClick: (e) => {
					e.stopPropagation();
				},
				style: { cursor: 'pointer', color: '#1677ff', textDecoration: 'underline' },
			}),
			render: (value: any, record: Baecha, index: number) => (
				<Popover
					placement="top"
					content={(
						<a onClick={onClosePopup}>
							{popupData?.memberInfo?.filter((item: any) => item.status === 'ok').length > 0 && (
								<div>
									{popupData?.memberInfo?.filter((item: any) => item.status === 'ok')
										.map((item: any) => <div key={item.name}>{item.name}</div>)}
								</div>
							)}
						</a>
					)}
					trigger="click"
					title={(
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<div>{popupData?.userInfo?.name || ''}</div>
							<div>{popupData?.userInfo?.phoneNum || ''}</div>
						</div>
					)}
					open={openPopover?.type === 'suju' && openPopover?.baechaId === record.id}
					onOpenChange={(bool) => onOpenPopup(bool, 'suju', record.id, record?.sujuUserId || '')}
				>
					<div className={styles.clickable}>{record.sujuMemberNickname}</div>
				</Popover>
			),
		},
		{
			title: '톤수',
			dataIndex: 'carType',
			key: 'carType',
			width: 50,
			align: 'center',
			render: (value: any) => value,
		},
		{
			title: '작업일시',
			dataIndex: 'workArriveDate',
			key: 'workArriveDate',
			width: 110,
			align: 'center',
			render: (value: any, record: Baecha, index: number) => (
				<div style={{ fontVariantNumeric: 'tabular-nums', color: record.baechaType === 'urgent' ? 'red' : '' }}>
					{`${dayjs(value).format('MM월 DD일')} ${dayjs(value).format('HH:mm')}`}
				</div>
			),
		},
		{
			title: (
				<>
					<div>작업</div>
					<div>기간</div>
				</>
			),
			dataIndex: 'workPeriod',
			key: 'workPeriod',
			width: 50,
			align: 'center',
			render: (value: any, record: Baecha, index: number) => (
				<div style={{ fontVariantNumeric: 'tabular-nums' }}>
					{getWorkPeriodStr(record)}
				</div>
			),
		},
		{
			title: '점수',
			dataIndex: 'point',
			key: 'point',
			width: 50,
			align: 'center',
			ellipsis: true,
			render: (value: any, record: Baecha, index: number) => (
				<div>{`${record.baljuPoint} / ${record.sujuPoint ? `-${record.sujuPoint}` : 0}`}</div>
			)
		},
		{
			title: '단가',
			dataIndex: 'priceInfo',
			key: 'priceInfo',
			width: 80,
			align: 'center',
			ellipsis: true,
			render: (value: any, record: Baecha, index: number) => (
				<div>{`${getShortBaechaPriceTypeStr(record.priceType)} ${record.price / 10000} / ${getBaechaPriceReceiveMethodStr(record.priceReceiveMethod)}`}</div>
			),
		},
		{
			title: '위치',
			dataIndex: 'workLoc',
			key: 'workLoc',
			width: 120,
			align: 'center',
			ellipsis: true,
			render: (value: any, record: Baecha, index: number) => (
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					{getRegionPrefix(record.region).length > 0 && (
						<span style={{ background: '#EFF1F4', color: '#4A5E7A', padding: '0 5px', borderRadius: '5px', marginRight: '5px' }}>
							{getRegionPrefix(record.region)}
						</span>
					)}
					{value}
				</div>
			),
		},
		{
			title: '내용',
			dataIndex: 'workContent',
			key: 'workContent',
			width: 100,
			align: 'center',
			ellipsis: true,
			render: (value: any, record: Baecha, index: number) => value,
		},
		{
			title: '전달',
			dataIndex: 'workEtc',
			key: 'workEtc',
			width: 100,
			align: 'center',
			ellipsis: true,
			render: (value: any, record: Baecha, index: number) => value,
		},
		{
			title: <div style={{ textAlign: 'center' }}>수수료</div>,
			dataIndex: 'fee',
			key: 'fee',
			width: 80,
			align: 'right',
			ellipsis: true,
			render: (value: any, record: Baecha, index: number) => (
				<div style={{ marginRight: '10px' }}>
					{formatCurrency(Number(value), 'KRW')}
				</div>
			),
		},
	];

	return (
		<Table
			className={styles.tableWrap}
			size="small"
			tableLayout="fixed"
			bordered
			columns={columns}
			onRow={(record) => {
				return {
					onClick: () => onClickData(record),
					style: { cursor: 'pointer' },
				}
			}}
			dataSource={list.map(row => ({...row, key: row.baechaNo}))}
			pagination={false}
		/>
)
}